<script>
	import { normalizeText, playerUrl } from './shared.js';

	export let team = null;
	export let onTeamClick = null;
	export let players = [];
	export let total = null;
	export let startTotal = null;
	export let benchTotal = null;
	export let statsExtra = null;
	export let tag = 'mlbl';
	export let compId = '';

	let sortField = 'isStart';
	let sortDirection = 'desc';

	$: isDefaultSort = sortField === 'isStart' && sortDirection === 'desc';
	$: sortedPlayers = sortPlayers(players, sortField, sortDirection);
	$: rows = buildRows(sortedPlayers, isDefaultSort, startTotal, benchTotal, total);

	function getStatValue(stats, field) {
		if (field === 'isStart') {
			return stats?.isStart ? 1 : 0;
		}
		if (field === 'rebound') {
			return (Number(stats?.defReb) || 0) + (Number(stats?.offReb) || 0);
		}
		return Number(stats?.[field]) || 0;
	}

	function sortPlayers(items, field, direction) {
		const dir = direction === 'asc' ? 1 : -1;
		return [...items].sort((left, right) => {
			const leftValue = getStatValue(left?.stats, field);
			const rightValue = getStatValue(right?.stats, field);
			return (leftValue - rightValue) * dir;
		});
	}

	function buildRows(list, defaultSort, startTotalStats, benchTotalStats, totalStats) {
		const result = [];
		if (defaultSort) {
			const starters = list.filter((player) => player?.stats?.isStart);
			const bench = list.filter((player) => !player?.stats?.isStart);
			starters.forEach((player) => result.push({ type: 'player', player, stats: player?.stats }));
			if (startTotalStats) {
				result.push({ type: 'summary', label: 'Стартовая пятерка', stats: startTotalStats });
			}
			bench.forEach((player) => result.push({ type: 'player', player, stats: player?.stats }));
			if (benchTotalStats) {
				result.push({ type: 'summary', label: 'Скамейка', stats: benchTotalStats });
			}
		} else {
			list.forEach((player) => result.push({ type: 'player', player, stats: player?.stats }));
		}
		if (totalStats) {
			result.push({ type: 'summary', label: 'Всего', stats: totalStats, isTotal: true });
		}
		return result;
	}

	function toggleSort(field) {
		if (sortField === field) {
			sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
			return;
		}
		sortField = field;
		sortDirection = field === 'isStart' ? 'desc' : 'asc';
	}

	function getSortLabel(field, label, activeField, activeDirection) {
		if (activeField !== field) {
			return label;
		}
		return `${label} ${activeDirection === 'asc' ? '↑' : '↓'}`;
	}

	function formatPlayedTime(seconds) {
		if (seconds === null || seconds === undefined) {
			return '—';
		}
		const totalSeconds = Number(seconds);
		if (!Number.isFinite(totalSeconds)) {
			return '—';
		}
		const minutes = Math.floor(totalSeconds / 60);
		const secs = totalSeconds % 60;
		return `${minutes}:${String(secs).padStart(2, '0')}`;
	}

	function getRebound(stats) {
		if (!stats) {
			return '—';
		}
		return (Number(stats.defReb) || 0) + (Number(stats.offReb) || 0);
	}

	function getPlayerNumber(player) {
		return normalizeText(player?.displayNumber || player?.playerNumber);
	}

	function getPlayerTitle(player) {
		return [player?.lastName, player?.firstName, player?.secondName].filter(Boolean).join(' ') || '—';
	}

	function getPlayerInitials(player, fallback = 'P') {
		const first = player?.firstName?.[0] || '';
		const last = player?.lastName?.[0] || '';
		return `${first}${last}`.trim() || fallback;
	}

	function getPlayerHref(player) {
		if (!player?.personId) {
			return '';
		}
		return playerUrl(player.personId, { tag, compId });
	}

	function getTeamName() {
		return team?.name || team?.shortName || team?.abcName || '';
	}

	function getTeamInitials() {
		return (team?.shortName?.[0] || team?.abcName?.[0] || team?.name?.[0] || 'T').toUpperCase();
	}

	function handleTeamClick() {
		if (onTeamClick) {
			onTeamClick();
		}
	}

	function formatShot(made, attempted) {
		if (made === null || made === undefined || attempted === null || attempted === undefined) {
			return '—';
		}
		return `${Number(made) || 0}/${Number(attempted) || 0}`;
	}

	function formatShotPercent(made, attempted) {
		const madeValue = Number(made) || 0;
		const attemptedValue = Number(attempted) || 0;
		if (!attemptedValue) {
			return '—';
		}
		return `${Math.round((madeValue / attemptedValue) * 100)}%`;
	}
</script>

<div class="protocol-team">
	<div class="protocol-team__head">
		<button type="button" class="protocol-team__link" on:click={handleTeamClick}>
			{#if team?.logo}
				<img class="protocol-team__logo" src={team.logo} alt={getTeamName()}>
			{:else}
				<div class="protocol-team__logo protocol-team__logo--placeholder">{getTeamInitials()}</div>
			{/if}
			<span class="protocol-team__name">{getTeamName()}</span>
		</button>
	</div>

	<div class="stats_table-wrap">
		<table class="stats_table protocol-table">
			<thead>
				<tr>
					<th rowspan="2" class="protocol-table__th-player">Игрок</th>
					<th rowspan="2" class="is-sortable" class:is-sorted={sortField === 'isStart'} on:click={() => toggleSort('isStart')}>
						{getSortLabel('isStart', '5', sortField, sortDirection)}
					</th>
					<th rowspan="2" class="is-sortable" class:is-sorted={sortField === 'second'} on:click={() => toggleSort('second')}>
						{getSortLabel('second', 'СВ', sortField, sortDirection)}
					</th>
					<th rowspan="2" class="is-sortable" class:is-sorted={sortField === 'points'} on:click={() => toggleSort('points')}>
						{getSortLabel('points', 'Очки', sortField, sortDirection)}
					</th>
					<th colspan="3">Броски</th>
					<th colspan="3">Подборы</th>
					<th rowspan="2" class="is-sortable" class:is-sorted={sortField === 'assist'} on:click={() => toggleSort('assist')}>
						{getSortLabel('assist', 'ГП', sortField, sortDirection)}
					</th>
					<th rowspan="2" class="is-sortable" class:is-sorted={sortField === 'steal'} on:click={() => toggleSort('steal')}>
						{getSortLabel('steal', 'ПХ', sortField, sortDirection)}
					</th>
					<th rowspan="2" class="is-sortable" class:is-sorted={sortField === 'turnover'} on:click={() => toggleSort('turnover')}>
						{getSortLabel('turnover', 'ПТ', sortField, sortDirection)}
					</th>
					<th rowspan="2" class="is-sortable" class:is-sorted={sortField === 'block'} on:click={() => toggleSort('block')}>
						{getSortLabel('block', 'БШ', sortField, sortDirection)}
					</th>
					<th rowspan="2" class="is-sortable" class:is-sorted={sortField === 'foul'} on:click={() => toggleSort('foul')}>
						{getSortLabel('foul', 'Ф', sortField, sortDirection)}
					</th>
					<th rowspan="2" class="is-sortable" class:is-sorted={sortField === 'foulsOn'} on:click={() => toggleSort('foulsOn')}>
						{getSortLabel('foulsOn', 'ФС', sortField, sortDirection)}
					</th>
					<th rowspan="2" class="is-sortable" class:is-sorted={sortField === 'plusMinus'} on:click={() => toggleSort('plusMinus')}>
						{getSortLabel('plusMinus', '+/-', sortField, sortDirection)}
					</th>
				</tr>
				<tr>
					<th class="is-sortable" class:is-sorted={sortField === 'goal2'} on:click={() => toggleSort('goal2')}>
						{getSortLabel('goal2', '2-очк', sortField, sortDirection)}
					</th>
					<th class="is-sortable" class:is-sorted={sortField === 'goal3'} on:click={() => toggleSort('goal3')}>
						{getSortLabel('goal3', '3-очк', sortField, sortDirection)}
					</th>
					<th class="is-sortable" class:is-sorted={sortField === 'goal1'} on:click={() => toggleSort('goal1')}>
						{getSortLabel('goal1', 'ШБ', sortField, sortDirection)}
					</th>
					<th class="is-sortable" class:is-sorted={sortField === 'defReb'} on:click={() => toggleSort('defReb')}>
						{getSortLabel('defReb', 'СЩ', sortField, sortDirection)}
					</th>
					<th class="is-sortable" class:is-sorted={sortField === 'offReb'} on:click={() => toggleSort('offReb')}>
						{getSortLabel('offReb', 'ЧЩ', sortField, sortDirection)}
					</th>
					<th class="is-sortable" class:is-sorted={sortField === 'rebound'} on:click={() => toggleSort('rebound')}>
						{getSortLabel('rebound', 'ВС', sortField, sortDirection)}
					</th>
				</tr>
			</thead>
			<tbody>
				{#each rows as row}
					<tr class:protocol-table__summary={row.type === 'summary'} class:protocol-table__summary--total={row.isTotal}>
						{#if row.type === 'player'}
							<td class="protocol-table__cell-player">
								<div class="protocol-player">
									<span class="protocol-player__number">{getPlayerNumber(row.player)}</span>
									<a class="protocol-player__identity" href={getPlayerHref(row.player) || undefined}>
										{#if row.player?.photo}
											<img class="protocol-player__photo" src={row.player.photo} alt={getPlayerTitle(row.player)}>
										{:else}
											<div class="protocol-player__placeholder">{getPlayerInitials(row.player, 'P')}</div>
										{/if}
										<span class="protocol-player__name">
											<span class="protocol-player__lastname">{row.player?.lastName || '—'}</span>
											<span class="protocol-player__firstname">{row.player?.firstName || ''}</span>
										</span>
									</a>
								</div>
							</td>
							<td class:is-sorted={sortField === 'isStart'}>{row.player?.stats?.isStart ? '✓' : ''}</td>
						{:else}
							<td class="protocol-table__cell-label">{row.label}</td>
							<td></td>
						{/if}
						<td class:is-sorted={sortField === 'second'}>{formatPlayedTime(row.stats?.second)}</td>
						<td class:is-sorted={sortField === 'points'}>{normalizeText(row.stats?.points)}</td>
						<td class="protocol-table__cell-shot" class:is-sorted={sortField === 'goal2'}>
							<span class="protocol-table__shot-main">{formatShot(row.stats?.goal2, row.stats?.shot2)}</span>
							<span class="protocol-table__shot-percent">{formatShotPercent(row.stats?.goal2, row.stats?.shot2)}</span>
						</td>
						<td class="protocol-table__cell-shot" class:is-sorted={sortField === 'goal3'}>
							<span class="protocol-table__shot-main">{formatShot(row.stats?.goal3, row.stats?.shot3)}</span>
							<span class="protocol-table__shot-percent">{formatShotPercent(row.stats?.goal3, row.stats?.shot3)}</span>
						</td>
						<td class="protocol-table__cell-shot" class:is-sorted={sortField === 'goal1'}>
							<span class="protocol-table__shot-main">{formatShot(row.stats?.goal1, row.stats?.shot1)}</span>
							<span class="protocol-table__shot-percent">{formatShotPercent(row.stats?.goal1, row.stats?.shot1)}</span>
						</td>
						<td class:is-sorted={sortField === 'defReb'}>{normalizeText(row.stats?.defReb)}</td>
						<td class:is-sorted={sortField === 'offReb'}>{normalizeText(row.stats?.offReb)}</td>
						<td class:is-sorted={sortField === 'rebound'}>{getRebound(row.stats)}</td>
						<td class:is-sorted={sortField === 'assist'}>{normalizeText(row.stats?.assist)}</td>
						<td class:is-sorted={sortField === 'steal'}>{normalizeText(row.stats?.steal)}</td>
						<td class:is-sorted={sortField === 'turnover'}>{normalizeText(row.stats?.turnover)}</td>
						<td class:is-sorted={sortField === 'block'}>{normalizeText(row.stats?.block)}</td>
						<td class:is-sorted={sortField === 'foul'}>{normalizeText(row.stats?.foul)}</td>
						<td class:is-sorted={sortField === 'foulsOn'}>{normalizeText(row.stats?.foulsOn)}</td>
						<td class:is-sorted={sortField === 'plusMinus'}>{normalizeText(row.stats?.plusMinus)}</td>
					</tr>
				{/each}
			</tbody>
		</table>
	</div>

	{#if statsExtra}
		<div class="protocol-metrics">
			<div class="protocol-metric">
				<span>Макс. преимущество</span>
				<strong>{normalizeText(statsExtra.maxLeading)}</strong>
			</div>
			<div class="protocol-metric">
				<span>Очки подряд</span>
				<strong>{normalizeText(statsExtra.maxPointsInRow)}</strong>
			</div>
			<div class="protocol-metric">
				<span>Быстрый прорыв</span>
				<strong>{normalizeText(statsExtra.fastBreakPoints)}</strong>
			</div>
			<div class="protocol-metric">
				<span>Очки в краске</span>
				<strong>{normalizeText(statsExtra.pointsInThePaint)}</strong>
			</div>
			<div class="protocol-metric">
				<span>Второй шанс</span>
				<strong>{normalizeText(statsExtra.secondChancePoints)}</strong>
			</div>
		</div>
	{/if}
</div>

<style>
	:host { display: block; }
	.protocol-team {
		margin-bottom: 28px;
	}
	.protocol-team__head {
		display: flex;
		align-items: center;
		margin-bottom: 12px;
	}
	.protocol-team__link {
		display: flex;
		align-items: center;
		gap: 10px;
		border: 0;
		background: transparent;
		padding: 0;
		font: inherit;
		cursor: pointer;
		text-align: left;
	}
	.protocol-team__logo {
		width: 36px;
		height: 36px;
		border-radius: 8px;
		object-fit: contain;
		background: #fff;
		border: 1px solid #e3e8ef;
	}
	.protocol-team__logo--placeholder {
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 14px;
		font-weight: 700;
		color: #00244d;
	}
	.protocol-team__name {
		font-size: 17px;
		font-weight: 700;
		color: #122033;
		transition: color 0.2s ease;
	}
	.protocol-team__link:hover .protocol-team__name {
		color: #0b4fa8;
	}
	.protocol-table {
		min-width: 980px;
	}
	.protocol-table th.is-sortable {
		cursor: pointer;
		user-select: none;
		transition: background-color 0.15s ease;
	}
	.protocol-table th.is-sortable:hover {
		background: #eef3fb;
	}
	.protocol-table th.is-sorted,
	.protocol-table td.is-sorted {
		background: #eaf1ff;
	}
	.protocol-table th.is-sorted {
		font-weight: 800;
		color: #0b4fa8;
	}
	.protocol-table td.is-sorted {
		font-weight: 700;
		color: #0b4fa8;
	}
	.protocol-table__th-player {
		text-align: left;
		min-width: 210px;
		position: sticky;
		left: 0;
		z-index: 3;
		background: #f7f9fc;
	}
	.protocol-table__cell-player {
		text-align: left;
		padding: 6px 10px !important;
		position: sticky;
		left: 0;
		z-index: 1;
		background: #fff;
	}
	.protocol-table__cell-label {
		text-align: left;
		font-weight: 800;
		color: #00244d;
		position: sticky;
		left: 0;
		z-index: 1;
		background: #f0f4fa;
	}
	.protocol-table__summary--total .protocol-table__cell-label {
		background: #e6edf8;
	}
	.protocol-table__summary td {
		background: #f0f4fa;
		font-weight: 700;
	}
	.protocol-table__summary--total td {
		background: #e6edf8;
		font-weight: 800;
		color: #00244d;
	}
	.protocol-player {
		display: flex;
		align-items: center;
		gap: 8px;
	}
	.protocol-player__identity {
		display: flex;
		align-items: center;
		gap: 8px;
		min-width: 0;
		text-decoration: none;
		color: inherit;
	}
	a.protocol-player__identity:hover .protocol-player__lastname {
		color: #0b4fa8;
	}
	.protocol-player__number {
		flex: 0 0 auto;
		min-width: 18px;
		font-weight: 700;
		color: #53657b;
		text-align: center;
	}
	.protocol-player__photo,
	.protocol-player__placeholder {
		width: 34px;
		height: 34px;
		border-radius: 7px;
		object-fit: cover;
		background: #edf2f7;
		flex: 0 0 34px;
	}
	.protocol-player__placeholder {
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 12px;
		font-weight: 700;
		color: #00244d;
	}
	.protocol-player__name {
		display: flex;
		flex-direction: column;
		line-height: 1.2;
		min-width: 0;
	}
	.protocol-player__lastname {
		font-weight: 700;
		color: #122033;
		font-size: 13px;
	}
	.protocol-player__firstname {
		color: #53657b;
		font-size: 12px;
	}
	.protocol-table__cell-shot {
		line-height: 1.2;
	}
	.protocol-table__shot-main {
		display: block;
		font-weight: 700;
		color: #122033;
	}
	.protocol-table__shot-percent {
		display: block;
		font-size: 11px;
		color: #6b7a90;
	}
	.protocol-metrics {
		display: grid;
		grid-template-columns: repeat(5, minmax(0, 1fr));
		gap: 10px;
		margin-top: 12px;
	}
	.protocol-metric {
		padding: 10px 12px;
		border-radius: 10px;
		background: #f7f9fc;
		border: 1px solid #eef1f6;
	}
	.protocol-metric span {
		display: block;
		margin-bottom: 4px;
		font-size: 11px;
		text-transform: uppercase;
		color: #6b7a90;
	}
	.protocol-metric strong {
		font-size: 15px;
		color: #122033;
	}
	@media (max-width: 640px) {
		.protocol-metrics {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}
</style>
